/**
 * @file api.ts
 * @brief TanStack Query hooks for Company Admin → Employees.
 *
 * @author DES
 * @date 19-06-2026
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  apiDelete,
  apiGet,
  apiPost,
  apiPut,
} from "@/shared/api/client";
import { companyEndpoints } from "@/shared/api/endpoints";
import {
  CompanyEmployeeSchema,
  CompanyEmployeesResponseSchema,
  LoginHistoryResponseSchema,
  type CompanyEmployee,
  type CreateEmployeeInput,
  type LoginEvent,
  type UpdateEmployeeInput,
} from "./schemas";
import {
  EnterpriseStatusSchema,
  type EnterpriseStatus,
} from "@/features/companies/enterpriseSchemas";

export const employeesKeys = {
  all: ["company", "employees"] as const,
  list: () => ["company", "employees", "list"] as const,
  detail: (id: string) => ["company", "employees", "detail", id] as const,
  loginHistory: (id: string) =>
    ["company", "employees", "login-history", id] as const,
  enterprise: () => ["company", "enterprise"] as const,
};

export function useEmployeesList(enabled = true) {
  return useQuery<CompanyEmployee[]>({
    queryKey: employeesKeys.list(),
    enabled,
    queryFn: async () => {
      const raw = await apiGet<unknown>(companyEndpoints.users);
      const parsed = CompanyEmployeesResponseSchema.parse(raw);
      return parsed.users;
    },
  });
}

export function useEmployeeLoginHistory(id: string | null, enabled = true) {
  return useQuery<LoginEvent[]>({
    queryKey: employeesKeys.loginHistory(id ?? ""),
    enabled: enabled && Boolean(id),
    queryFn: async () => {
      const raw = await apiGet<unknown>(
        companyEndpoints.userLoginHistory(id!),
      );
      return LoginHistoryResponseSchema.parse(raw).history;
    },
  });
}

export function useEmployee(id: string | null, enabled = true) {
  return useQuery<CompanyEmployee>({
    queryKey: employeesKeys.detail(id ?? ""),
    enabled: enabled && Boolean(id),
    queryFn: async () => {
      const raw = await apiGet<unknown>(companyEndpoints.userById(id!));
      return CompanyEmployeeSchema.parse(raw);
    },
  });
}

export function useCreateEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateEmployeeInput) => {
      const raw = await apiPost<unknown>(companyEndpoints.users, input);
      const parsed = CompanyEmployeesResponseSchema.safeParse(raw);
      if (parsed.success && parsed.data.users?.[0]) {
        return parsed.data.users[0];
      }
      const row = (raw as { user?: unknown }).user;
      return CompanyEmployeeSchema.parse(row);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: employeesKeys.all });
    },
  });
}

export function useUpdateEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      input,
    }: {
      id: string;
      input: UpdateEmployeeInput;
    }) => {
      const raw = await apiPut<unknown>(companyEndpoints.userById(id), input);
      const row = (raw as { user?: unknown }).user ?? raw;
      return CompanyEmployeeSchema.parse(row);
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: employeesKeys.all });
      void qc.invalidateQueries({ queryKey: employeesKeys.detail(vars.id) });
    },
  });
}

export function useUpdateEmployeeStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      status,
    }: {
      id: string;
      status: "active" | "suspended" | "deleted";
    }) => {
      const raw = await apiPut<unknown>(companyEndpoints.userStatus(id), {
        status,
      });
      const row = (raw as { user?: unknown }).user ?? raw;
      return CompanyEmployeeSchema.parse(row);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: employeesKeys.all });
    },
  });
}

export function useDeleteEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiDelete(companyEndpoints.userById(id));
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: employeesKeys.all });
    },
  });
}

export function useAssignEmployeeCard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      paymentMethodId,
    }: {
      id: string;
      paymentMethodId: string | null;
    }) => {
      await apiPut(companyEndpoints.userPaymentMethod(id), { paymentMethodId });
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: employeesKeys.all });
      void qc.invalidateQueries({ queryKey: employeesKeys.detail(vars.id) });
    },
  });
}

export function useResetEmployeePassword() {
  return useMutation({
    mutationFn: async ({
      id,
      newPassword,
    }: {
      id: string;
      newPassword: string;
    }) => {
      await apiPost(companyEndpoints.userPassword(id), { newPassword });
    },
  });
}

export function useResendEmployeeWelcome() {
  return useMutation({
    mutationFn: async (id: string) => {
      await apiPost(companyEndpoints.userResendWelcome(id));
    },
  });
}

export function useCompanyEnterpriseStatus(enabled = true) {
  return useQuery<EnterpriseStatus>({
    queryKey: employeesKeys.enterprise(),
    enabled,
    queryFn: async () => {
      const raw = await apiGet<unknown>(companyEndpoints.enterprise);
      return EnterpriseStatusSchema.parse(raw);
    },
  });
}

export function useAssignEnterpriseSeat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      const raw = await apiPost<unknown>(companyEndpoints.enterpriseSeats, {
        userId,
      });
      return EnterpriseStatusSchema.parse(raw);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: employeesKeys.all });
      void qc.invalidateQueries({ queryKey: employeesKeys.enterprise() });
    },
  });
}

export function useRevokeEnterpriseSeat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      const raw = await apiDelete<unknown>(
        companyEndpoints.enterpriseSeatByUser(userId),
      );
      return EnterpriseStatusSchema.parse(raw);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: employeesKeys.all });
      void qc.invalidateQueries({ queryKey: employeesKeys.enterprise() });
    },
  });
}

/** Company admin downloads one employee's data as a zip (GDPR, 05-10-2026). */
export function useDownloadEmployeeData() {
  return useMutation({
    mutationFn: async ({ id, email }: { id: string; email: string }) => {
      const { downloadExportZip } = await import("@/features/profile/api");
      const day = new Date().toISOString().slice(0, 10);
      const who = email.replace(/[^A-Za-z0-9._-]+/g, "_");
      return downloadExportZip(companyEndpoints.userExport(id), `verblizr-data-${who}-${day}.zip`);
    },
  });
}
